import { useAuth, useUser } from '@clerk/react';
import { Link } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Button,
  Group,
  Stack,
  Card,
  SimpleGrid,
  Badge,
  Center,
  Image
} from '@mantine/core';
import {
  IconShoppingBag,
  IconBuildingStore,
  IconBoxSeam,
  IconUsers
} from '@tabler/icons-react';
import { useSession } from '../api';
import PrincessCarousel from '../components/PrincessCarousel';

const Home = () => {
  const { isSignedIn } = useAuth();
  const { user: clerkUser } = useUser();
  const { isAdmin, isSeller, authextra } = useSession();
  const isAuthenticated = isSignedIn;
  const user = {
    username: authextra?.display_name || clerkUser?.firstName || '',
    is_seller: isSeller,
    is_staff: isAdmin,
  };

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl" align="center">
        {/* Hero Section */}
        <Center>
          <Stack gap="md" align="center" maw={600}>
            {/* Logo */}
            <Image
              src="/pp-logo.png"
              alt="PediPiggy Logo"
              width={200}
              height={200}
              fit="contain"
              mb="md"
            />

            <Title order={1} ta="center" size="3rem" c="pink">
              {!isAuthenticated ? (
                <span className="welcome-title-animated">
                  {"Welcome to PediPiggy".split("").map((char, index) => (
                    <span key={index}>{char === " " ? "\u00A0" : char}</span>
                  ))}
                </span>
              ) : (
                "Welcome to PediPiggy"
              )}
            </Title>

            <Text size="xl" ta="center" c="dimmed">
              Get paid for showing off your freshly painted piggies
            </Text>

            {!isAuthenticated && (
              <Stack gap="md" align="center">
                <Group mt="lg">
                  <Button
                    component={Link}
                    to="/piggy-subscription"
                    size="lg"
                    color="pink"
                    leftSection={<IconShoppingBag size="1.2rem" />}
                  >
                    Show me your PIGGIE$
                  </Button>
                  <Button
                    component={Link}
                    to="/become-princess"
                    variant="outline"
                    size="lg"
                    leftSection={<IconBuildingStore size="1.2rem" />}
                  >
                    Become a PEDI PRINCESS
                  </Button>
                </Group>
                <Button
                  component={Link}
                  to="/princesses"
                  variant="light"
                  size="lg"
                  color="grape"
                >
                  Browse PEDI PRINCESSES
                </Button>
              </Stack>
            )}
          </Stack>
        </Center>

        {/* User-specific content */}
        {isAuthenticated && (
          <Card shadow="sm" padding="xl" radius="md" withBorder maw={800} w="100%">
            <Stack gap="lg">
              <Group>
                <Title order={2}>Welcome back, {user.username}!</Title>
                <Group gap="xs">
                  {user.is_seller && (
                    <Badge color="green" variant="light" size="lg">
                      Seller
                    </Badge>
                  )}
                  {user.is_staff && (
                    <Badge color="pink" variant="light" size="lg">
                      Admin
                    </Badge>
                  )}
                </Group>
              </Group>

              <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                <Card withBorder padding="lg">
                  <Stack gap="md" align="center">
                    <IconShoppingBag size="2rem" color="var(--mantine-color-pink-6)" />
                    <Title order={4}>Browse Products</Title>
                    <Text size="sm" ta="center" c="dimmed">
                      Discover amazing products from verified princesses
                    </Text>
                    <Button component={Link} to="/products" variant="light" fullWidth>
                      Shop Now
                    </Button>
                  </Stack>
                </Card>

                <Card withBorder padding="lg">
                  <Stack gap="md" align="center">
                    <IconBuildingStore size="2rem" color="var(--mantine-color-green-6)" />
                    <Title order={4}>Find Princesses</Title>
                    <Text size="sm" ta="center" c="dimmed">
                      Connect with trusted princesses in your area
                    </Text>
                    <Button component={Link} to="/princesses" variant="light" color="green" fullWidth>
                      View Princesses
                    </Button>
                  </Stack>
                </Card>

                {user.is_seller ? (
                  <Card withBorder padding="lg">
                    <Stack gap="md" align="center">
                      <IconBoxSeam size="2rem" color="var(--mantine-color-grape-6)" />
                      <Title order={4}>My Products</Title>
                      <Text size="sm" ta="center" c="dimmed">
                        Manage your product listings and sales
                      </Text>
                      <Button component={Link} to="/seller/products" variant="light" color="grape" fullWidth>
                        Manage Products
                      </Button>
                    </Stack>
                  </Card>
                ) : (
                  <Card withBorder padding="lg">
                    <Stack gap="md" align="center">
                      <IconBuildingStore size="2rem" color="var(--mantine-color-yellow-6)" />
                      <Title order={4}>Become a Princess</Title>
                      <Text size="sm" ta="center" c="dimmed">
                        Start selling your items on PediPiggy
                      </Text>
                      <Button component={Link} to="/become-seller" variant="light" color="yellow" fullWidth>
                        Get Started
                      </Button>
                    </Stack>
                  </Card>
                )}

                {user.is_staff && (
                  <Card withBorder padding="lg">
                    <Stack gap="md" align="center">
                      <IconUsers size="2rem" color="var(--mantine-color-orange-6)" />
                      <Title order={4}>Admin Panel</Title>
                      <Text size="sm" ta="center" c="dimmed">
                        Manage users, sellers, and platform settings
                      </Text>
                      <Button component={Link} to="/admin" variant="light" color="orange" fullWidth>
                        Admin Dashboard
                      </Button>
                    </Stack>
                  </Card>
                )}
              </SimpleGrid>
            </Stack>
          </Card>
        )}

        {/* Features */}
        <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg" maw={900} w="100%">
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Stack gap="md" align="center">
              <IconBuildingStore size="2rem" color="var(--mantine-color-pink-6)" />
              <Title order={3} size="h4">Verified Princesses</Title>
              <Text size="sm" ta="center">
                All our princesses are verified and approved to ensure quality and trust.
              </Text>
            </Stack>
          </Card>

          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Stack gap="md" align="center">
              <IconShoppingBag size="2rem" color="var(--mantine-color-green-6)" />
              <Title order={3} size="h4">Quality Products</Title>
              <Text size="sm" ta="center">
                Browse thousands of products across multiple categories with detailed descriptions.
              </Text>
            </Stack>
          </Card>

          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Stack gap="md" align="center">
              <IconBoxSeam size="2rem" color="var(--mantine-color-grape-6)" />
              <Title order={3} size="h4">Easy Selling</Title>
              <Text size="sm" ta="center">
                List your products easily with our intuitive seller dashboard and management tools.
              </Text>
            </Stack>
          </Card>
        </SimpleGrid>

        {/* Princess Carousel */}
        <PrincessCarousel />

        {/* Categories */}
        <Stack gap="lg" w="100%" maw={900}>
          <Title order={2} ta="center" size="h2">
            Categories
          </Title>
          <Card 
            shadow="xl" 
            padding="xl" 
            radius="md" 
            withBorder 
            component="a" 
            href="/browse-categories"
            style={{ 
              textDecoration: 'none', 
              transition: 'transform 0.2s, box-shadow 0.2s',
              cursor: 'pointer',
              background: 'linear-gradient(135deg, rgba(159,56,204,0.1) 0%, rgba(233,45,134,0.1) 100%)'
            }}
            className="category-card"
          >
            <Stack gap="md" align="center">
              <Text size="4rem">🦶</Text>
              <Title order={3} ta="center" c="grape">
                Browse All Foot Categories
              </Title>
              <Text size="md" ta="center" c="dimmed">
                Explore princesses by foot attributes, nail polish colors, pedicure types, and more!
              </Text>
              <Button color="grape" size="md" mt="sm">
                Explore Categories
              </Button>
            </Stack>
          </Card>
        </Stack>
      </Stack>
    </Container>
  );
};

export default Home;
